import { useState, useRef } from "react";
import { Landmark, Copy, Check, Mail, User, Hash, Smartphone, CreditCard, FileText, BellRing, Loader2, Paperclip, ImageUp, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TRANSFER, PLAN_LABEL, PLAN_AMOUNT } from "../lib/transfer";

function CopyRow({ icon: Icon, label, value, display, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("No se pudo copiar"); }
  };
  return (
    <div className="flex items-center justify-between gap-3 bg-white rounded-xl px-3 py-2.5 border border-stone-100">
      <div className="min-w-0 flex items-center gap-2.5">
        <Icon size={16} className="text-brand-sage shrink-0" />
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/45">{label}</p>
          <p className="font-semibold text-brand-ink text-sm break-all">{display || value}</p>
        </div>
      </div>
      <button data-testid={testid} onClick={copy} className="shrink-0 p-1.5 rounded-lg text-brand-terracotta hover:bg-brand-terracotta/10 transition-all" title="Copiar">
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}

export default function TransferDetails({ plan = "pro" }) {
  const { user } = useAuth();
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const fileRef = useRef(null);
  const planLabel = PLAN_LABEL[plan] || "tu plan";
  const amount = PLAN_AMOUNT[plan] || "";
  const concept = `ClinPed ${planLabel}`;
  const subject = `Comprobante de pago ClinPed - ${planLabel}`;
  const body = `Hola, adjunto mi comprobante de transferencia para activar mi cuenta de ClinPed.\n\nPlan: ${planLabel}${amount ? ` ($${amount} MXN)` : ""}\nCorreo de mi cuenta: ${user?.email || ""}\n\n(Adjunta aquí tu comprobante)`;
  const mailto = `mailto:${TRANSFER.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const notifyPaid = async () => {
    setNotifying(true);
    try {
      const fd = new FormData();
      fd.append("plan", plan);
      if (receipt) fd.append("file", receipt);
      await api.post("/payments/transfer/notify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotified(true);
      toast.success(receipt
        ? "¡Listo! Recibimos tu aviso y comprobante. Activaremos tu cuenta al validarlo."
        : "¡Listo! Avisamos al equipo de tu pago. Activaremos tu cuenta al validar el comprobante.");
    } catch {
      toast.error("No se pudo enviar el aviso. Envía tu comprobante por correo, por favor.");
    } finally { setNotifying(false); }
  };

  const pickReceipt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("El comprobante supera 15 MB"); return; }
    setReceipt(f);
  };

  return (
    <div className="space-y-4" data-testid="transfer-details">
      <div className="rounded-2xl bg-brand-sand/25 p-4 space-y-2">
        <div className="flex items-center gap-2 text-brand-ink font-outfit font-bold text-sm">
          <Landmark size={16} className="text-brand-sage" /> Datos para transferencia (SPEI)
        </div>
        <div className="space-y-2">
          <CopyRow icon={Landmark} label="Banco" value={TRANSFER.bank} testid="transfer-bank" />
          <CopyRow icon={User} label="Beneficiario" value={TRANSFER.holder} testid="transfer-holder" />
          <CopyRow icon={Hash} label="CLABE" value={TRANSFER.clabe} testid="transfer-clabe" />
          <CopyRow icon={CreditCard} label="No. de cuenta" value={TRANSFER.account} testid="transfer-account" />
          <CopyRow icon={Smartphone} label="Transferencia por celular / CoDi" value={TRANSFER.phone} display="55 3328 5931" testid="transfer-phone" />
          <CopyRow icon={FileText} label="Concepto" value={concept} testid="transfer-concept" />
        </div>
        {amount && (
          <div className="flex items-baseline justify-between pt-1 px-1">
            <span className="text-sm text-brand-ink/60 font-semibold">Monto ({planLabel})</span>
            <span className="font-outfit font-extrabold text-brand-ink text-xl" data-testid="transfer-amount">${amount} <span className="text-sm font-semibold">MXN/mes</span></span>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-brand-terracotta/25 bg-brand-terracotta/[0.05] p-4">
        <p className="text-sm text-brand-ink/80 leading-relaxed">
          <b>Paso 2:</b> Adjunta la foto de tu comprobante y avísanos. Activaremos tu cuenta en cuanto lo validemos.
        </p>

        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" onChange={pickReceipt} className="hidden" data-testid="transfer-receipt-input" />
        {receipt ? (
          <div className="mt-3 flex items-center gap-2 bg-white border border-brand-sage/30 rounded-xl px-3 py-2" data-testid="transfer-receipt-chip">
            <ImageUp size={16} className="text-brand-sage shrink-0" />
            <span className="text-xs font-semibold text-brand-ink truncate flex-1">{receipt.name}</span>
            <button onClick={() => { setReceipt(null); if (fileRef.current) fileRef.current.value = ""; }} className="p-1 rounded-lg text-muted-foreground hover:text-destructive" title="Quitar"><X size={14} /></button>
          </div>
        ) : (
          <button data-testid="transfer-attach-btn" onClick={() => fileRef.current?.click()}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed border-brand-sage/50 text-brand-sageDark hover:bg-brand-sage/5 font-outfit font-semibold text-sm py-2.5 rounded-2xl transition-all">
            <Paperclip size={15} /> Adjuntar foto del comprobante
          </button>
        )}

        <button data-testid="transfer-notify-paid" onClick={notifyPaid} disabled={notifying || notified}
          className="mt-2 w-full flex items-center justify-center gap-2 bg-brand-sage hover:bg-brand-sageDark text-white font-outfit font-semibold text-sm py-3 rounded-2xl transition-all disabled:opacity-70">
          {notified ? <><Check size={16} /> Aviso enviado</> : notifying ? <><Loader2 size={16} className="animate-spin" /> Enviando…</> : <><BellRing size={16} /> Ya realicé mi transferencia (avisar)</>}
        </button>

        <div className="mt-3 pt-3 border-t border-brand-terracotta/15">
          <p className="text-xs text-brand-ink/55 mb-2">¿Prefieres enviarlo por correo?</p>
          <a href={mailto} data-testid="transfer-send-receipt"
            className="w-full flex items-center justify-center gap-2 border border-brand-terracotta/40 text-brand-terracotta hover:bg-brand-terracotta/5 font-outfit font-semibold text-sm py-2.5 rounded-2xl transition-all">
            <Mail size={16} /> Enviar mi comprobante por correo
          </a>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 text-xs text-brand-ink/60">
          <span className="break-all">{TRANSFER.supportEmail}</span>
        </div>
      </div>
    </div>
  );
}
